import { useState, useEffect, useMemo, useCallback } from 'react';

import NextIcon from '@/components/ui/icons/trainers/NextIcon';

import { ITrainersContent } from '@/types/trainers';
import { ApiErrorEnum } from '@/types/api';
import { IDetection } from '@/types/features';
import ErrorMessage from '@/components/ui/trainers/shared/ErrorMessage';
import { DisplayOnMap } from '@/components/ui/trainers/shared/DisplayOnMap';
import ImageCarousel from './ImageCarousel';

interface Props {
  content?: ITrainersContent;
}

const DisplayMapFeatureCandidateContent: React.FC<Props> = ({ content }) => {
  const landmarks = useMemo(
    () => Object.keys(content?.candidateFeatures || {}),
    [content],
  );
  const [landmarkIndex, setLandmarkIndex] = useState(0);
  const [selectedLandmark, setSelectedLandmark] = useState<string | undefined>(
    landmarks[landmarkIndex],
  );
  const [imageHeight, setImageHeight] = useState(0);

  const { lat, lon, azimuth } = content || {};

  const handleNextLandmark = useCallback(() => {
    setLandmarkIndex(prev => {
      if (prev + 1 >= landmarks.length) {
        return 0;
      } else {
        return prev + 1;
      }
    });
  }, [setLandmarkIndex, landmarks.length]);

  const handlePrevLandmark = useCallback(() => {
    setLandmarkIndex(prev => {
      if (prev - 1 < 0) {
        return landmarks.length - 1;
      } else {
        return prev - 1;
      }
    });
  }, [setLandmarkIndex, landmarks.length]);

  useEffect(() => {
    setSelectedLandmark(landmarks[landmarkIndex]);
  }, [landmarkIndex]);

  const getLandmarkFramesAndDetections = useCallback(
    (landmark: string): { images: string[]; detections: IDetection[] } => {
      if (
        !content ||
        !content.candidateFeatures ||
        !content.candidateFeatures[landmark]
      ) {
        return {
          images: [],
          detections: [],
        };
      }

      const detectionsByFrame =
        content.candidateFeatures[landmark]?.detectionsByFrame ?? {};

      const frames = content.candidateFeatures[landmark]?.frames?.filter(
        frame => String(frame.idx) in detectionsByFrame,
      );

      return {
        images: frames.map(frame => frame.url),
        detections: frames.map(
          frame => detectionsByFrame[String(frame.idx)][0].detectionInfo,
        ),
      };
    },
    [],
  );

  const selectedLandmarkFramesAndDetections: {
    images: string[];
    detections: IDetection[];
  } = useMemo(() => {
    if (!selectedLandmark) {
      return { images: [], detections: [] };
    }

    return getLandmarkFramesAndDetections(selectedLandmark);
  }, [selectedLandmark, content, landmarkIndex]);

  return (
    <div className="align-center flex justify-center">
      {!content ? (
        <ErrorMessage error={ApiErrorEnum.NoContent} />
      ) : !selectedLandmark ? (
        <ErrorMessage error={ApiErrorEnum.InvalidFeature} />
      ) : (
        <div>
          <div className="mr-[50%] flex flex-col items-center">
            <div className="flex items-center gap-2 text-lg font-bold">
              Landmark
            </div>
            <div className="flex flex-row items-center">
              <NextIcon
                className="rotate-180 cursor-pointer hover:opacity-90"
                style={{
                  transition: 'opacity 250ms cubic-bezier(0.4,0,0.2,1)',
                  fontSize: 36,
                }}
                onClick={handlePrevLandmark}
              />
              <div className="container flex flex-1 flex-col p-4">
                {landmarkIndex + 1}/{landmarks.length}
              </div>
              <NextIcon
                className="cursor-pointer hover:opacity-90"
                style={{
                  transition: 'opacity 250ms cubic-bezier(0.4,0,0.2,1)',
                  fontSize: 36,
                }}
                onClick={handleNextLandmark}
              />
            </div>
          </div>
          <div className="flex flex-row items-start p-4">
            <div className="flex flex-1 flex-col">
              <ImageCarousel
                key={selectedLandmark}
                images={selectedLandmarkFramesAndDetections.images}
                detections={selectedLandmarkFramesAndDetections.detections}
                onImageLoaded={setImageHeight}
                startWithIndex={'first'}
              />
            </div>

            <div className="container flex flex-1">
              {imageHeight ? (
                <div
                  className="relative w-full"
                  style={{
                    height: imageHeight,
                  }}>
                  <DisplayOnMap
                    target={{ azimuth, lat, lon }}
                    points={[]}
                    options={{
                      hasAppleMaps: false,
                      hideTargetMarker: false,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayMapFeatureCandidateContent;
